import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { DatePicker, DateTimePicker } from "@/components/DatePicker";
import { AvailabilityView } from "@/components/Availability";
import { api, formatErr, isMember } from "@/lib/api";

export const MemberGate = ({ compact = false }) => (
  <div data-testid="member-gate" className={`rounded-md border border-amber-200 bg-amber-50 ${compact ? "p-4" : "p-6"}`}>
    <div className="flex items-start gap-3">
      <Lock className="mt-0.5 h-4 w-4 text-amber-700" />
      <div>
        <p className="text-sm font-medium text-amber-900">Membership required</p>
        <p className="mt-1 text-sm text-amber-800">Activate a membership plan to request interviews, hire candidates and download business files.</p>
        <Link to="/app/membership" data-testid="member-gate-cta" className="mt-3 inline-block text-sm font-medium text-slate-900 underline underline-offset-4">View membership plans</Link>
      </div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-mono uppercase tracking-wider text-stone-500">{label}</Label>{children}</div>
);

const useSubmit = (url, onDone) => {
  const [busy, setBusy] = useState(false);
  const submit = async (payload, msg) => {
    setBusy(true);
    try {
      await api.post(url, payload);
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, submit };
};

export const HireDialog = ({ candidate, open, onOpenChange }) => {
  const { user } = useAuth();
  const [f, setF] = useState({ engagement: "Full-time", hours_per_week: 40, start_date: "", notes: "" });
  const { busy, submit } = useSubmit("/hire-requests", () => onOpenChange(false));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hire-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Request to hire {candidate?.name}</DialogTitle>
          <DialogDescription>Our recruitment team will confirm availability and contract details within 48 hours.</DialogDescription>
        </DialogHeader>
        {!isMember(user) ? <MemberGate compact /> : (
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Engagement">
                <Select value={f.engagement} onValueChange={(v) => setF({ ...f, engagement: v })}>
                  <SelectTrigger data-testid="hire-engagement-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Full-time", "Part-time", "Project-based"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Hours / week">
                <Input data-testid="hire-hours-input" type="number" min={1} max={60} value={f.hours_per_week} onChange={(e) => setF({ ...f, hours_per_week: Number(e.target.value) })} />
              </Field>
            </div>
            <Field label="Preferred start date">
              <DatePicker testId="hire-start-date-input" value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} />
            </Field>
            <Field label="Role details / notes">
              <Textarea data-testid="hire-notes-input" rows={4} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Main responsibilities, tools, working hours overlap…" />
            </Field>
          </div>
        )}
        {isMember(user) && (
          <DialogFooter>
            <Button data-testid="hire-submit-button" disabled={busy} onClick={() => submit({ candidate_id: candidate.id, ...f }, "Hire request sent to our team")}>
              {busy ? "Sending…" : "Send hire request"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const InterviewDialog = ({ candidate, open, onOpenChange }) => {
  const { user } = useAuth();
  const [times, setTimes] = useState(["", "", ""]);
  const [notes, setNotes] = useState("");
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { busy, submit } = useSubmit("/interviews", () => onOpenChange(false));
  const chosen = times.filter(Boolean);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="interview-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Interview {candidate?.name}</DialogTitle>
          <DialogDescription>Suggest up to three time slots ({tz}). We'll confirm one and send a meeting link.</DialogDescription>
        </DialogHeader>
        {!isMember(user) ? <MemberGate compact /> : (
          <div className="grid gap-4">
            {candidate?.availability_schedule && (
              <div className="rounded-md bg-stone-50 p-3" data-testid="interview-availability-hint">
                <p className="mb-2 text-xs font-medium text-stone-700">{candidate.name?.split(" ")[0]}'s availability</p>
                <AvailabilityView schedule={candidate.availability_schedule} compact />
              </div>
            )}
            {times.map((t, i) => (
              <Field key={i} label={`Option ${i + 1}${i === 0 ? " (required)" : ""}`}>
                <DateTimePicker testId={`interview-time-input-${i}`} value={t} onChange={(v) => setTimes(times.map((x, j) => (j === i ? v : x)))} />
              </Field>
            ))}
            <Field label="Notes for the interview">
              <Textarea data-testid="interview-notes-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Topics you'd like to cover…" />
            </Field>
          </div>
        )}
        {isMember(user) && (
          <DialogFooter>
            <Button data-testid="interview-submit-button" disabled={busy || !chosen.length} onClick={() => submit({ candidate_id: candidate.id, preferred_times: chosen, timezone: tz, notes }, "Interview request sent")}>
              {busy ? "Sending…" : "Request interview"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
